// Screen 11: Q&A History — all your past questions to AI across the library.
// Layout: sidebar | (topbar + filter rail | list | answer detail rail)

function QuestionsScreen() {
  const { t, lang } = useT();
  const ko = lang === 'ko';
  const nav = React.useContext(NavContext);

  const all = (typeof QUESTIONS !== 'undefined' ? QUESTIONS : []);
  const cats = (typeof QA_CATEGORIES !== 'undefined' ? QA_CATEGORIES : []);

  const [cat, setCat] = React.useState('all');
  const [scope, setScope] = React.useState('all'); // 'all' | 'paper' | 'global'
  const [starOnly, setStarOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [activeId, setActiveId] = React.useState(all[0]?.id || null);

  // Filtering
  const filtered = React.useMemo(() => {
    let xs = all.slice();
    if (cat !== 'all')   xs = xs.filter(q => q.cat === cat);
    if (scope === 'paper')  xs = xs.filter(q => q.paperId);
    if (scope === 'global') xs = xs.filter(q => !q.paperId);
    if (starOnly) xs = xs.filter(q => q.starred);
    if (search.trim()) {
      const s = search.toLowerCase();
      xs = xs.filter(q => (q.q + q.a + (q.qEn||'') + (q.aEn||'')).toLowerCase().includes(s));
    }
    return xs;
  }, [all, cat, scope, starOnly, search]);

  React.useEffect(() => {
    if (filtered.length && !filtered.find(q => q.id === activeId)) {
      setActiveId(filtered[0].id);
    }
  }, [filtered, activeId]);

  const active = all.find(q => q.id === activeId) || null;

  // Resolve paper title for a question id
  const papers = (typeof PAPERS !== 'undefined' ? PAPERS : []);
  const paperOf = (pid) => papers.find(p => p.id === pid);

  // counts per category
  const counts = {};
  cats.forEach(c => {
    counts[c.id] = c.id === 'all' ? all.length : all.filter(q => q.cat === c.id).length;
  });

  return (
    <div className="pp pp-screen" data-screen-label="11 Q&A history">
      <Sidebar active="questions"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), ko ? '내 Q&A' : 'My Q&A']}/>}
          search={ko ? '내 질문·답변 전체 검색…' : 'Search across your questions and answers…'}
        />

        <div className="pp-content">
          {/* LEFT: category filter rail */}
          <RailLeft id="qa-filters" defaultWidth={240} min={200} max={340} className="pp-qa-filter-rail scroll">
            <div className="section-title" style={{ marginBottom: 12 }}>
              <span className="num">{ko ? '필터' : 'Filters'}</span>
            </div>

            <div className="pp-qa-stat">
              <div className="serif" style={{ fontSize: 30, lineHeight: 1, color: 'var(--fg-0)' }}>{all.length}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
                {ko ? '총 질문 수' : 'total questions'}
              </div>
              <div className="pp-qa-stat-row mono">
                <span><I name="quote" size={9}/> {all.reduce((s, q) => s + (q.sources || 0), 0)}{ko ? ' 출처' : ' sources'}</span>
                <span><I name="star" size={9}/> {all.filter(q => q.starred).length}{ko ? ' 스타' : ' starred'}</span>
              </div>
            </div>

            <div className="pp-qa-filter-group">
              <div className="pp-qa-filter-label">{ko ? '카테고리' : 'Category'}</div>
              <div className="col gap-1">
                {cats.map(c => (
                  <button key={c.id}
                    className={'pp-qa-cat-row' + (cat === c.id ? ' is-on' : '')}
                    onClick={() => setCat(c.id)}>
                    <span className="pp-qa-cat-tick"/>
                    <span style={{ flex: 1, fontSize: 12.5 }}>{ko ? c.ko : c.en}</span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{counts[c.id]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pp-qa-filter-group">
              <div className="pp-qa-filter-label">{ko ? '범위' : 'Scope'}</div>
              <div className="pp-seg">
                <button className={'pp-seg-btn ' + (scope === 'all' ? 'is-on' : '')} onClick={() => setScope('all')}>{ko ? '전체' : 'All'}</button>
                <button className={'pp-seg-btn ' + (scope === 'paper' ? 'is-on' : '')} onClick={() => setScope('paper')}>{ko ? '논문' : 'Paper'}</button>
                <button className={'pp-seg-btn ' + (scope === 'global' ? 'is-on' : '')} onClick={() => setScope('global')}>{ko ? '일반' : 'Global'}</button>
              </div>
            </div>

            <div className="pp-qa-filter-group">
              <div className="pp-qa-filter-label">{ko ? '시그널' : 'Signals'}</div>
              <button className={'pp-qa-toggle' + (starOnly ? ' is-on' : '')} onClick={() => setStarOnly(v => !v)}>
                <I name="star" size={11}/>
                <span>{ko ? '스타한 질문만' : 'Starred only'}</span>
                <div className={'pp-toggle ' + (starOnly ? 'is-on' : '')}>
                  <div className="pp-toggle-knob"/>
                </div>
              </button>
            </div>

            <div className="pp-qa-filter-group">
              <div className="pp-qa-filter-label">{ko ? '기간' : 'Date'}</div>
              <DatePillsLocal/>
            </div>

            <div className="pp-qa-filter-group" style={{ marginTop: 'auto', paddingTop: 14, borderTop: '1px solid var(--border-1)' }}>
              <button className="btn sm primary" style={{ width: '100%' }}><I name="plus" size={11}/> {ko ? '새 질문' : 'New question'}</button>
              <button className="btn sm ghost" style={{ width: '100%', marginTop: 6 }}><I name="download" size={11}/> {ko ? '내보내기 (Markdown)' : 'Export (Markdown)'}</button>
            </div>
          </RailLeft>

          {/* CENTER: question list */}
          <div className="pp-qa-center scroll">
            <div className="pp-qa-center-head">
              <div className="col gap-1">
                <div className="row gap-3" style={{ alignItems: 'baseline' }}>
                  <span className="serif" style={{ fontSize: 24, color: 'var(--fg-0)' }}>
                    {ko ? '내 Q&A 기록' : 'My Q&A history'}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                    {ko ? `${filtered.length}건 · 최신순` : `${filtered.length} results · newest first`}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg-2)', maxWidth: 720 }}>
                  {ko
                    ? 'AI에 물어본 모든 질문과 답변, 인용 출처까지. 카테고리·범위·기간으로 필터.'
                    : 'Every question you asked AI, the answer it gave, and the cited sources. Filter by category, scope, or date.'}
                </div>
              </div>
              <div className="row gap-2">
                <div className="pp-qa-search">
                  <I name="search" size={13} style={{ color: 'var(--fg-3)' }}/>
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={ko ? '키워드 검색' : 'Search keyword'}
                  />
                  {search && (
                    <button className="iconbtn" style={{ width: 22, height: 22 }} onClick={() => setSearch('')}>
                      <I name="close" size={11}/>
                    </button>
                  )}
                </div>
                <button className="btn sm"><I name="sort" size={11}/> {ko ? '최신순' : 'Recent'}</button>
              </div>
            </div>

            {/* result list */}
            <div className="pp-qa-result-list">
              {filtered.length === 0 && (
                <div className="pp-qa-empty">
                  <I name="question" size={24} style={{ color: 'var(--fg-3)' }}/>
                  <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 8 }}>
                    {ko ? '조건에 맞는 질문이 없습니다.' : 'No questions match these filters.'}
                  </div>
                </div>
              )}
              {filtered.map(q => {
                const cc = cats.find(c => c.id === q.cat);
                const p = q.paperId ? paperOf(q.paperId) : null;
                return (
                  <button key={q.id}
                    className={'pp-qa-result-row' + (q.id === activeId ? ' is-active' : '')}
                    onClick={() => setActiveId(q.id)}>
                    <div className="col gap-2" style={{ flex: 1, minWidth: 0 }}>
                      <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span className="chip accent" style={{ height: 19 }}>{ko ? cc?.ko : cc?.en}</span>
                        {p ? (
                          <span className="chip" style={{ height: 19 }}>
                            <I name="book" size={9}/>
                            <span className="ellipsis" style={{ maxWidth: 260 }}>{p.title.split(':')[0]}</span>
                          </span>
                        ) : (
                          <span className="chip violet" style={{ height: 19 }}>
                            <I name="globe" size={9}/> {ko ? '일반' : 'global'}
                          </span>
                        )}
                        {q.starred && <I name="star" size={11} style={{ color: 'var(--warn)' }}/>}
                        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)', marginLeft: 'auto' }}>
                          {ko ? q.when : q.whenEn}
                        </span>
                      </div>
                      <div style={{ fontSize: 14, color: 'var(--fg-0)', fontWeight: 500, lineHeight: 1.45, textAlign: 'left' }}>
                        {ko ? q.q : q.qEn}
                      </div>
                      <div className="pp-qa-row-preview" style={{ textAlign: 'left' }}>
                        {(ko ? q.a : q.aEn).slice(0, 168)}…
                      </div>
                      <div className="row gap-3 mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>
                        {q.sources > 0 && (
                          <span><I name="quote" size={9} style={{ verticalAlign: -1 }}/> {q.sources}{ko ? ' 출처' : ' sources'}</span>
                        )}
                        {q.follow?.length > 0 && (
                          <span><I name="chevronRight" size={9} style={{ verticalAlign: -1 }}/> {q.follow.length}{ko ? ' 후속' : ' follow-up'}</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT: answer detail */}
          <RailRight id="qa-detail" defaultWidth={420} min={340} max={560} className="pp-qa-detail-rail scroll">
            {active ? <QADetailPane q={active} paper={paperOf(active.paperId)} ko={ko} nav={nav}/> : null}
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function QADetailPane({ q, paper, ko, nav }) {
  const cats = (typeof QA_CATEGORIES !== 'undefined' ? QA_CATEGORIES : []);
  const cat = cats.find(c => c.id === q.cat);
  return (
    <div className="col gap-4">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="section-title"><span className="num">{ko ? '질문 상세' : 'Question detail'}</span></div>
        <div className="row gap-1">
          <button className="iconbtn" title={ko ? '복사' : 'Copy'}><I name="copy" size={13}/></button>
          <button className="iconbtn" title={ko ? '내보내기' : 'Export'}><I name="download" size={13}/></button>
          <button className="iconbtn" title={ko ? '북마크' : 'Bookmark'}><I name="bookmark" size={13}/></button>
        </div>
      </div>

      <div className="pp-qa-detail-card">
        <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span className="chip accent" style={{ height: 20 }}>{ko ? cat?.ko : cat?.en}</span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{ko ? q.when : q.whenEn}</span>
          {q.starred && <I name="star" size={12} style={{ color: 'var(--warn)' }}/>}
        </div>
        <div className="serif" style={{ fontSize: 18, lineHeight: 1.3, color: 'var(--fg-0)', marginTop: 10, letterSpacing: '-0.01em' }}>
          {ko ? q.q : q.qEn}
        </div>
      </div>

      {paper && (
        <div className="pp-qa-detail-paper">
          <div className="section-title" style={{ marginBottom: 8 }}>{ko ? '대상 논문' : 'About paper'}</div>
          <div className="pp-qa-detail-paper-card" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}>
            <div className="pp-mini-pdf-sm"/>
            <div className="col grow gap-1" style={{ minWidth: 0 }}>
              <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                <span className="chip accent" style={{ height: 18 }}>{paper.venue}</span>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{paper.date}</span>
              </div>
              <div className="serif ellipsis" style={{ fontSize: 13, color: 'var(--fg-0)' }}>{paper.title}</div>
              <div className="ellipsis" style={{ fontSize: 11, color: 'var(--fg-2)' }}>{paper.authors.slice(0, 3).join(', ')}</div>
            </div>
            <I name="external" size={12} style={{ color: 'var(--fg-3)' }}/>
          </div>
        </div>
      )}

      <div className="pp-qa-detail-answer">
        <div className="row gap-2" style={{ marginBottom: 10, alignItems: 'baseline' }}>
          <I name="sparkles" size={12} style={{ color: 'var(--acc-violet)' }}/>
          <span style={{ fontSize: 11, color: 'var(--acc-violet)', letterSpacing: '0.08em', textTransform: 'uppercase' }} className="mono">
            {ko ? 'AI 답변' : 'AI answer'}
          </span>
          {q.sources > 0 && (
            <span className="chip cyan mono" style={{ height: 18, marginLeft: 'auto' }}>
              <I name="quote" size={9}/> {q.sources}{ko ? ' 출처 검증됨' : ' grounded'}
            </span>
          )}
        </div>
        <div className="pp-qa-detail-answer-body">{ko ? q.a : q.aEn}</div>
      </div>

      {q.follow && q.follow.length > 0 && (
        <div className="pp-qa-detail-follow">
          <div className="section-title" style={{ marginBottom: 8 }}>{ko ? '내가 이어 물은 것' : 'You followed up with'}</div>
          <div className="col gap-2">
            {q.follow.map((f, i) => (
              <button key={i} className="pp-qa-follow-q">
                <I name="chevronRight" size={11} style={{ color: 'var(--acc-indigo)' }}/>
                <span>{f}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="row gap-2" style={{ marginTop: 6 }}>
        <button className="btn sm primary grow"><I name="sparkles" size={11}/> {ko ? '이 답변에서 이어 묻기' : 'Continue from here'}</button>
        <button className="btn sm"><I name="book" size={11}/> {ko ? '위키로' : 'To wiki'}</button>
      </div>
    </div>
  );
}

function DatePillsLocal() {
  const [v, setV] = React.useState('14d');
  return (
    <div className="row gap-1" style={{ flexWrap: 'wrap' }}>
      {['24h','7d','14d','30d','all'].map(p => (
        <button key={p} className={'pp-pill ' + (p === v ? 'is-on' : '')} onClick={() => setV(p)}>{p}</button>
      ))}
    </div>
  );
}

// styles
if (typeof document !== 'undefined' && !document.getElementById('pp-questions-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-questions-styles';
  s.textContent = `
  .pp-qa-filter-rail {
    width: 240px; flex-shrink: 0;
    border-right: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 18px 14px 14px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 18px;
  }
  .pp-qa-stat {
    padding: 14px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    display: flex; flex-direction: column;
  }
  .pp-qa-stat-row {
    display: flex; gap: 12px;
    margin-top: 10px; padding-top: 10px;
    border-top: 1px solid var(--border-1);
    font-size: 10.5px;
    color: var(--fg-2);
  }
  .pp-qa-filter-group {
    display: flex; flex-direction: column; gap: 8px;
  }
  .pp-qa-filter-label {
    font-size: 10.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--fg-3);
    padding: 0 4px;
  }
  .pp-qa-cat-row {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 8px;
    border-radius: 5px;
    color: var(--fg-1);
    background: transparent;
    border: 0;
    cursor: pointer;
    font: inherit;
    text-align: left;
  }
  .pp-qa-cat-row:hover { background: rgba(255,255,255,0.025); color: var(--fg-0); }
  .pp-qa-cat-row.is-on {
    background: rgba(136,150,255,0.10);
    color: var(--fg-0);
  }
  .pp-qa-cat-tick {
    width: 4px; height: 14px;
    background: transparent;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .pp-qa-cat-row.is-on .pp-qa-cat-tick {
    background: var(--acc-indigo);
    box-shadow: 0 0 6px var(--acc-indigo);
  }
  .pp-qa-toggle {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    color: var(--fg-1);
    font-size: 11.5px;
    cursor: pointer;
    text-align: left;
  }
  .pp-qa-toggle.is-on { border-color: rgba(232,184,106,0.40); color: var(--fg-0); }
  .pp-qa-toggle > span { flex: 1; }
  .pp-qa-toggle .pp-toggle { margin-left: auto; }

  .pp-qa-center {
    flex: 1; min-width: 0;
    padding: 24px 28px 32px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 18px;
  }
  .pp-qa-center-head {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 18px;
  }
  .pp-qa-search {
    display: flex; align-items: center; gap: 8px;
    height: 30px; padding: 0 10px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
    border-radius: 7px;
    transition: border-color .12s;
    min-width: 240px;
  }
  .pp-qa-search:focus-within { border-color: rgba(136,150,255,0.40); }
  .pp-qa-search input { flex: 1; font-size: 12px; color: var(--fg-0); }
  .pp-qa-search input::placeholder { color: var(--fg-3); }

  .pp-qa-result-list {
    display: flex; flex-direction: column; gap: 10px;
  }
  .pp-qa-result-row {
    display: flex;
    padding: 14px 16px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    cursor: pointer;
    text-align: left;
    font: inherit; color: inherit;
    transition: border-color .12s, background .12s;
  }
  .pp-qa-result-row:hover { border-color: var(--border-2); }
  .pp-qa-result-row.is-active {
    border-color: rgba(136,150,255,0.45);
    background: linear-gradient(180deg, rgba(136,150,255,0.05), transparent 80%);
    box-shadow: 0 0 0 1px rgba(136,150,255,0.25), 0 0 30px rgba(136,150,255,0.08);
  }
  .pp-qa-row-preview {
    font-size: 12px;
    color: var(--fg-2);
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .pp-qa-empty {
    padding: 60px 20px;
    text-align: center;
    border: 1px dashed var(--border-1);
    border-radius: 12px;
    display: flex; flex-direction: column; align-items: center;
  }

  .pp-qa-detail-rail {
    width: 420px; flex-shrink: 0;
    border-left: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 24px 18px 28px;
    overflow: auto;
  }
  .pp-qa-detail-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    padding: 14px 16px;
  }
  .pp-qa-detail-paper-card {
    display: flex; gap: 12px; align-items: center;
    padding: 10px 12px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 9px;
    cursor: pointer;
    transition: border-color .12s;
  }
  .pp-qa-detail-paper-card:hover { border-color: rgba(136,150,255,0.40); }
  .pp-mini-pdf-sm {
    width: 30px; height: 38px; flex-shrink: 0;
    background: linear-gradient(180deg, #2a2e3e, #1a1d28);
    border: 1px solid var(--border-2);
    border-radius: 2px;
    position: relative;
  }
  .pp-mini-pdf-sm::before {
    content: ''; position: absolute; left: 5px; right: 5px; top: 6px;
    height: 1px; background: rgba(255,255,255,0.10);
    box-shadow: 0 3px 0 rgba(255,255,255,0.10), 0 6px 0 rgba(136,150,255,0.20), 0 9px 0 rgba(255,255,255,0.10);
  }

  .pp-qa-detail-answer {
    padding: 14px 16px;
    background: linear-gradient(180deg, rgba(180,155,255,0.04), transparent 70%);
    border: 1px solid rgba(180,155,255,0.20);
    border-radius: 10px;
  }
  .pp-qa-detail-answer-body {
    font-size: 13px;
    color: var(--fg-1);
    line-height: 1.7;
  }
  .pp-qa-detail-follow {
    padding-top: 14px;
    border-top: 1px solid var(--border-1);
  }
  `;
  document.head.appendChild(s);
}

export { QuestionsScreen };
